import { ExportSection } from '../maintenance-dashboard-interfaces/maintenance-dashboard.interface';

/**
 * Sentinel option value: selects reps with off-boarding initiated rather than a
 * status, because off-boarding is a stage that spans Inforced and Serving Notice.
 */
export const OFFBOARDING_FILTER = 'OFFBOARDING_INITIATED';

/** Separator joining a section id and a field key into a selection id. */
const ID_SEPARATOR = '::';

/** Builds the `sectionId::fieldKey` identity used for selection and history. */
export function exportFieldId(sectionId: string, fieldKey: string): string {
  return `${sectionId}${ID_SEPARATOR}${fieldKey}`;
}

/**
 * Slug for a provider name, so a provider's field key survives the list being
 * reordered or added to (an index would not, and history stores these keys).
 */
// function providerFieldKey(name: string): string {
//   return `userCode_${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
// }

// /** One user-code column per insurance provider, in the provider list's order. */
// const providerFields: ExportField[] = INSURANCE_PROVIDERS.map((provider) => ({
//   key: providerFieldKey(provider.name),
//   label: `${provider.name} — User Code`
// }));

/**
 * Every column the export dialog can offer, grouped by maintenance section. The
 * section ids and titles mirror `MaintenanceaceViewComponent.sections`, and the
 * field keys mirror the form controls each section actually renders.
 *
 * The sheet is built server-side by `exportMaintanancedata`, which reads the
 * adviser's full maintenance record — so every column here is populated, not just
 * the ones the dashboard list endpoint happens to return.
 *
 * ⚠️ `field` is the backend's column key and is manually kept in sync with the Go
 * structs. A key renamed there silently exports a blank column here; cross-check
 * against the backend repo when the record's shape changes.
 */
export const EXPORT_SECTIONS: ExportSection[] = [
  {
    id: 'Management',
    title: 'Advisor & Branch Details',
    icon: 'groups',
    fields: [
      { section: "Basic Info", label: "Full Name (as in NRIC)", field: "full_name_nric" },
      { section: "Basic Info", label: "Preferred Name", field: "preferred_name" },
      { section: "Basic Info", label: "Designation", field: "designation" },
      { section: "Basic Info", label: "Branch", field: "branch" },
      { section: "Management", label: "Financial Service Director", field: "finacial_service_dir" },
      { section: "Management", label: "Financial Service Manager", field: "finacial_service_man" },
      { section: "Management", label: "Name of Referral", field: "name_of_referal" },
      { section: "Management", label: "Tier", field: "tier" },
      { section: "Management", label: "Corporate Email Address", field: "corporate_email_address" },
      { section: "Maintenance", label: "Affinity Employee ID", field: "mn_affinity_employee_id" },
      { section: "Maintenance", label: "Corporate Mobile", field: "mn_corporate_mobile" },
      { section: "Maintenance", label: "Corporate Email (Singlife)", field: "mn_corporate_email_singlife" },
      { section: "Management", label: "RNF No.", field: "rnf_no" },
      { section: "Management", label: "RNF Date", field: "rnf_date" },
      { section: "Management", label: "PIAS Manager Code", field: "pias_man_code" },
      { section: "Management", label: "PIAS Rep Code", field: "pias_rep_code" },
      { section: "Management", label: "Agent Channel", field: "agent_channel" },
      { section: "Management", label: "Rep Designation", field: "rep_designation" },
      { section: "Management", label: "Commission Representative", field: "commission_reprsentatitve" },
      { section: "Management", label: "Commission Introducer", field: "commission_introducer" },
      { section: "Corporate Details", label: "Lodgment Date", field: "lodgment_date" },
      { section: "Maintenance", label: "Rep Status", field: "mn_rep_status" },
      { section: "Maintenance", label: "Date Resigned (PIAS)", field: "mn_date_resigned_pias" },
      { section: "Maintenance", label: "Last Day of Service (PIAS)", field: "mn_last_day_of_service_with_pias" },
      { section: "Maintenance", label: "Reason for Leaving", field: "mn_reason_for_leaving" },
      { section: "Maintenance", label: "Affinity Model", field: "mn_affinity_model" },
    ]
  },
  {
    id: 'BasicInfo',
    title: 'Personal Details',
    icon: 'person',
    fields: [
      { section: "Basic Info", label: "NRIC No.", field: "nric_no" },
      { section: "Basic Info", label: "Date of Birth", field: "date_of_birth" },
      { section: "Basic Info", label: "Address", field: "address" },
      { section: "Basic Info", label: "Personal Email Address", field: "personal_email_address" },
      { section: "Basic Info", label: "Gender", field: "gender" },
      { section: "Basic Info", label: "Nationality", field: "nationality" },
      { section: "Basic Info", label: "Place of Birth", field: "place_of_birth" },
      { section: "Basic Info", label: "Race", field: "race" },
      { section: "Basic Info", label: "Marital Status", field: "marital_status" },
      { section: "Basic Info", label: "Mobile Number", field: "mobile_number" },
      { section: "Basic Info", label: "Home Number", field: "home_number" },
      { section: "Basic Info", label: "FIN / Passport No.", field: "fin_passport_no" },
    ]
  },
  {
    id: 'SpouseDetails',
    title: 'Spouse Details',
    icon: 'wc',
    fields: [
      { section: "Spouse Details", label: "Spouse Full Name (as in NRIC)", field: "spouse_name_nric" },
      { section: "Spouse Details", label: "Spouse NRIC No.", field: "spouse_nric_no" },
      { section: "Spouse Details", label: "Spouse Date of Birth", field: "spouse_dob" },
      { section: "Spouse Details", label: "Spouse FIN / Passport No.", field: "spouse_fin_passport_no" },
      { section: "Spouse Details", label: "Spouse Nationality", field: "spouse_nationality" },
      { section: "Spouse Details", label: "Spouse Occupation", field: "spouse_occupation" },
      { section: "Spouse Details", label: "Name of Employer", field: "name_of_employer" },
    ]
  },
  {
    id: 'BankDetails',
    title: 'Bank Details',
    icon: 'account_balance',
    fields: [
      { section: "Bank Details", label: "Bank Name", field: "bankname" },
      { section: "Bank Details", label: "Bank Branch Name", field: "bank_branch_name" },
      { section: "Bank Details", label: "Bank Account Number", field: "bank_account_number" },
      // { section: "Bank Details", label: "Bank N/A", field: "bank_not_applicable" },
    ]
  },
  {
    id: 'NationalService',
    title: 'National Service',
    icon: 'engineering',
    fields: [
      { section: "Licensing", label: "Not Applicable", field: "not_applicable" },
      { section: "National Service", label: "National Service Commitment", field: "national_service_commitment" },
      { section: "National Service", label: "Service Status", field: "service_status" },
      { section: "National Service", label: "Enlistment Date", field: "enlistment_date" },
      { section: "National Service", label: "ORD Date", field: "ord_date" },
    ]
  },
  {
    id: 'Licensing',
    title: 'Licences & Modules',
    icon: 'workspace_premium',
    fields: [
      { section: "Licensing", label: "Advice CIS", field: "advice_cis" },
      { section: "Licensing", label: "Advice Life", field: "advice_life" },
      { section: "Licensing", label: "BSC Grade", field: "bsc_grade" },
      { section: "Licensing", label: "BSC Satisfactory", field: "bsc_satisfactory" },
      { section: "Licensing", label: "BSC Grade 1", field: "bsc_grade_1" },
      { section: "Licensing", label: "BSC Satisfactory 1", field: "bsc_statisfactory_1" },
      { section: "Licensing", label: "M5 RES5", field: "m5_res5" },
      { section: "Licensing", label: "M9", field: "m9" },
      { section: "Licensing", label: "M8 Rep", field: "m8_rep" },
      { section: "Licensing", label: "BCP", field: "bcp" },
      { section: "Licensing", label: "M6 Rep", field: "m6_rep" },
      { section: "Licensing", label: "M9A", field: "m9a" },
      { section: "Licensing", label: "M8A Rep", field: "m8a_rep" },
      { section: "Licensing", label: "PGI", field: "pgi" },
      { section: "Licensing", label: "M6A Rep", field: "m6a_rep" },
      { section: "Licensing", label: "CM LIP", field: "cm_lip" },
      { section: "Licensing", label: "CM CIS", field: "cm_cis" },
      { section: "Licensing", label: "Comm GI", field: "commgi" },
      { section: "Licensing", label: "CM CMP", field: "cm_cmp" },
      { section: "Licensing", label: "HI", field: "hi" },
      { section: "Licensing", label: "CM LIC", field: "cm_lic" },
      { section: "Licensing", label: "Cert in GI", field: "cert_in_gi" },
    ]
  },
  {
    id: 'Maintenance',
    title: 'Others',
    icon: 'more_horiz',
    fields: [
      { section: "Maintenance", label: "Advice in GI", field: "mn_advice_in_gi" },
      { section: "Maintenance", label: "Advice in Securities", field: "mn_advice_in_securities" },
      { section: "Maintenance", label: "LOU on Register of Interests in Securities", field: "mn_lou_on_register_of_interests_in_securities" },
      { section: "Maintenance", label: "Incentive Scheme", field: "mn_incentive_scheme" },
      { section: "Maintenance", label: "Gainful Employment", field: "mn_gainful_employment" },
      { section: "Maintenance", label: "Lodged with Adverse", field: "mn_lodged_with_adverse" },
      { section: "Maintenance", label: "Misconduct Lodge", field: "mn_misconduct_lodge" },
      { section: "Maintenance", label: "Rep BSC Grade B or Worse (Last 4 Quarters)", field: "mn_bsc_grade_b_or_worse_last_4_quarters_rep" },
      { section: "Maintenance", label: "Sup BSC Satisfactory or Worse (Last 4 Quarters)", field: "mn_bsc_satisfactory_or_worse_last_4_quarters_sup" },
      { section: "Maintenance", label: "MSU Agreement Version", field: "mn_msu_agreement_version" },
      { section: "Maintenance", label: "Authorisation Card Issued", field: "mn_authorisation_card_issued" },
      { section: "Maintenance", label: "Vesting Rights 90 Days", field: "mn_vesting_rights_90_days" },
      { section: "Maintenance", label: "Authorisation Card Returned & Destroyed", field: "mn_authorisation_card_returned_and_destroyed" },
      { section: "Maintenance", label: "Transfer Instructions", field: "mn_transfer_instructions" },
      { section: "Maintenance", label: "Vaccination Status", field: "mn_vaccination_status" },
      { section: "Maintenance", label: "Under Credit or Close Monitoring", field: "mn_under_credit_or_close_monitoring" },
      { section: "Maintenance", label: "Highest Qualifications", field: "mn_highest_qualifications" },
      { section: "Maintenance", label: "Professional Certificates", field: "mn_professional_certificates" },
    ]
  }
];

/** Rep-status filters offered by the dialog, in tile order. */
export const EXPORT_STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'All Status', value: '' },
  // Values match `MaintenanceListDashboard.repstatus`, not their display labels.
  { label: 'Inforce', value: 'Inforced' },
  { label: 'Serving Notice', value: 'Serving Notice' },
  { label: 'Resigned', value: 'Resigned' },
  { label: 'Off-boarding', value: OFFBOARDING_FILTER }
];